/**
 * Authentication (ADR-015, ADR-023).
 *
 * Accounts exist for two reasons, in this order of importance to the user:
 *   1. Accurate resolution — model-assisted matching is signed-in only, because
 *      it costs money on a public endpoint (ADR-023).
 *   2. Coming back — dated, append-only assessments so the delta over time is
 *      visible (R13, ADR-022).
 *
 * Multi-user, single-subject: every account sees only its own data (ADR-015).
 *
 * On error messages: a friendly string is what the user should read, but a
 * friendly string that hides `e.code` makes a misconfigured Firebase project
 * indistinguishable from a wrong password. Every failure therefore logs the raw
 * code to the console, and in dev the code is appended to the visible message.
 */
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut,
  GoogleAuthProvider, signInWithPopup, onAuthStateChanged,
} from "firebase/auth";
import { auth, isFirebaseConfigured } from "./firebase";
import { useEffect, useState } from "react";

export { isFirebaseConfigured };

export function useAuth() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(!isFirebaseConfigured);
  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (u) => { setUser(u); setReady(true); });
  }, []);
  return { user, ready };
}

// Setup failures — the project is wrong, not the user's input. These are the
// ones the generic fallback used to swallow.
const SETUP = {
  "operation-not-allowed":
    "Sign-in isn't switched on for this project yet. Enable Email/Password and Google in Firebase → Authentication → Sign-in method.",
  "unauthorized-domain":
    "This domain isn't authorised for sign-in. Add it in Firebase → Authentication → Settings → Authorized domains.",
  "configuration-not-found":
    "Firebase Authentication hasn't been set up on this project. Open Firebase → Authentication and click Get started.",
  "api-key-not-valid":
    "The Firebase API key in .env.local isn't valid for this project.",
  "invalid-api-key":
    "The Firebase API key in .env.local isn't valid for this project.",
  "network-request-failed":
    "Couldn't reach Firebase. Check the network and any content blocker.",
  "popup-blocked":
    "The browser blocked the sign-in window. Allow pop-ups for this site, or use email and password.",
};

const friendly = (e) => {
  const c = e?.code || "";
  const bare = c.replace(/^auth\//, "");

  let msg = SETUP[bare];
  if (!msg) {
    if (bare.includes("email-already-in-use")) msg = "That email already has an account — try signing in.";
    else if (bare.includes("invalid-credential") || bare.includes("wrong-password") || bare.includes("user-not-found"))
      msg = "Email or password doesn't match.";
    else if (bare.includes("weak-password")) msg = "Use at least six characters.";
    else if (bare.includes("invalid-email")) msg = "That doesn't look like an email address.";
    else if (bare.includes("popup-closed") || bare.includes("cancelled-popup")) msg = "Sign-in window closed.";
    else if (bare.includes("too-many-requests")) msg = "Too many attempts. Wait a minute and try again.";
    else msg = "Couldn't sign you in. Try again in a moment.";
  }

  // Always log the truth. The console is the diagnostic path; the card is the UX.
  // eslint-disable-next-line no-console
  console.error("[auth]", c || "(no code)", e?.message || e);

  return import.meta.env.DEV && c ? `${msg}  (${c})` : msg;
};

export async function signUp(email, password) {
  try { return { user: (await createUserWithEmailAndPassword(auth, email, password)).user }; }
  catch (e) { return { error: friendly(e) }; }
}
export async function signIn(email, password) {
  try { return { user: (await signInWithEmailAndPassword(auth, email, password)).user }; }
  catch (e) { return { error: friendly(e) }; }
}
export async function signInWithGoogle() {
  try { return { user: (await signInWithPopup(auth, new GoogleAuthProvider())).user }; }
  catch (e) { return { error: friendly(e) }; }
}
export const logOut = () => signOut(auth);
/** Right to erasure (ADR-026): append-only is history WITHIN an account, never a defence against deletion. */
// deleteAccount() used to live here and did half the job: it removed the
// Firebase Auth user and left every Firestore document behind, unreachable but
// undeleted. Erasure now lives in lib/account.js, which clears the data first
// and the credentials last — the only order the rules allow (ADR-039).
